import React from 'react';

import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { GridRowSelectionModel } from '@mui/x-data-grid';

import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import LinkIcon from '@mui/icons-material/Link';

import ActivityBudgetRegisterGrid from './ActivityBudgetRegisterGrid';
import BudgetAllocationActivityGrid from './BudgetAllocationActivityGrid';
import BudgetAllocationBulkDialog from './BudgetAllocationBulkDialog';
import BudgetAllocationFilterBar from './BudgetAllocationFilterBar';
import BudgetAllocationLineItemGrid from './BudgetAllocationLineItemGrid';
import BudgetAllocationLinksGrid from './BudgetAllocationLinksGrid';
import BudgetAllocationSummaryPanel from './BudgetAllocationSummaryPanel';

import {
  fetchActivities,
  fetchActivityBudgetRegister,
  fetchActivityBudgetWorkbench,
  fetchCostCenters,
  fetchLineItems,
  fetchLinks,
  processAllocation,
  unmapLinks,
  transitionActivityBudget,
} from './budgetallocation.apis';

import {
  AllocationMode,
  IActivityBudgetHeader,
  IActivityBudgetRegister,
  IAllocationActivity,
  IAllocationBudgetControl,
  IAllocationCostCenter,
  IAllocationLineItem,
  IAllocationLink,
  IAllocationSummary,
} from './budgetallocation.models';

import { fetchPlanningJourneyPhases } from '../core/planningJourney.cache';
import { PlanningWorkbenchShell } from '../planningcore';
import FPlanningJourney from '../core/FPlanningJourney';

/* ============================================================
 * SELECTION HELPERS
 * ============================================================ */

const emptySelection = (): GridRowSelectionModel =>
  ({
    type: 'include',
    ids: new Set(),
  }) as GridRowSelectionModel;

const selectionIds = (selection: GridRowSelectionModel | any): string[] => {
  if (!selection) return [];

  if (selection.ids && typeof selection.ids[Symbol.iterator] === 'function') {
    return Array.from(selection.ids as Iterable<any>).map(String);
  }

  if (Array.isArray(selection)) {
    return selection.map(String);
  }

  return [];
};

const selectionCount = (selection: GridRowSelectionModel | any): number =>
  selectionIds(selection).length;

/* ============================================================
 * GOVERNED ACTION CONTRACT
 * ============================================================ */

type ActivityBudgetActions = {
  canSubmit: boolean;
  canReopen: boolean;
  canAllocate: boolean;
  canUnmap: boolean;
  canRefresh: boolean;
  canExit: boolean;
  budgetPlanReady: boolean;
  hasBudgetControl: boolean;
};

const emptyActions = (): ActivityBudgetActions => ({
  canSubmit: false,
  canReopen: false,
  canAllocate: false,
  canUnmap: false,
  canRefresh: false,
  canExit: false,
  budgetPlanReady: false,
  hasBudgetControl: false,
});

const normalizeActions = (
  value: Partial<ActivityBudgetActions> | null | undefined
): ActivityBudgetActions => ({
  canSubmit: value?.canSubmit === true,
  canReopen: value?.canReopen === true,
  canAllocate: value?.canAllocate === true,
  canUnmap: value?.canUnmap === true,
  canRefresh: value?.canRefresh === true,
  canExit: value?.canExit === true,
  budgetPlanReady: value?.budgetPlanReady === true,
  hasBudgetControl: value?.hasBudgetControl === true,
});

/* ============================================================
 * STATE
 * ============================================================ */

type State = {
  loading: boolean;
  error: string;
  success: string;
  warning: string;

  phases: any[];

  costcenters: IAllocationCostCenter[];
  selectedCostCenter: number | null;

  activitySearch: string;
  lineItemSearch: string;

  register: IActivityBudgetRegister[];
  summary: IAllocationSummary;

  header: IActivityBudgetHeader;
  actions: ActivityBudgetActions;

  activities: IAllocationActivity[];
  lineitems: IAllocationLineItem[];
  links: IAllocationLink[];

  selectedActivities: GridRowSelectionModel;
  selectedLineItems: GridRowSelectionModel;
  selectedLinks: GridRowSelectionModel;

  dialogOpen: boolean;

  control: IAllocationBudgetControl;
};

/* ============================================================
 * WORKBENCH
 * ============================================================ */

class BudgetAllocationWorkbench extends React.Component<any, State> {
  state: State = {
    loading: false,
    error: '',
    success: '',
    warning: '',

    phases: [],

    costcenters: [],
    selectedCostCenter: null,

    activitySearch: '',
    lineItemSearch: '',

    register: [],
    summary: {},

    header: {},
    actions: emptyActions(),

    activities: [],
    lineitems: [],
    links: [],

    selectedActivities: emptySelection(),
    selectedLineItems: emptySelection(),
    selectedLinks: emptySelection(),

    dialogOpen: false,

    control: {
      controlareacode: 'ACTIVITY',
      ceilingamount: 0,
      allocatedamount: 0,
      availableamount: 0,
      utilisationpercent: 0,
      warningpercent: 90,
      controlstatus: 'UNAVAILABLE',
    },
  };

  /* ============================================================
   * CONTEXT
   * ============================================================ */

  private getPlanningCycleId = (): number => {
    return Number(this.props.plancontext?.pci || 0);
  };

  get budgetplanid(): number | null {
    const costcenterid = this.state.selectedCostCenter;

    if (!costcenterid) return null;

    const cc = this.state.costcenters.find(
      (x) => Number(x.recid) === Number(costcenterid)
    );

    const id = Number(cc?.budgetplanid ?? 0);

    return id > 0 ? id : null;
  }

  private getLifecycleState = (): string => {
    return String(this.state.header.statecode || '').toUpperCase();
  };

  private canMutate = (): boolean => {
    return this.getLifecycleState() === 'DRAFT';
  };

  /* ============================================================
   * LIFECYCLE
   * ============================================================ */

  componentDidMount() {
    this.initialize();
  }

  private initialize = async () => {
    const pci = this.getPlanningCycleId();

    if (!pci) {
      this.setState({
        error:
          'Planning Cycle context is missing. Please set your preferred context in Planning Setup.',
      });
      return;
    }

    this.setState({
      loading: true,
      error: '',
      success: '',
    });

    try {
      const phases = await fetchPlanningJourneyPhases();

      this.setState({ phases });

      await this.loadInitial();
    } catch (error: any) {
      this.setState({
        error: error?.message || String(error),
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  /* ============================================================
   * INITIAL LOAD
   *
   * Register  = financial readiness authority
   * Workbench = lifecycle capability authority
   * ============================================================ */

  loadInitial = async () => {
    this.setState({
      loading: true,
      error: '',
      success: '',
    });

    try {
      const pci = this.getPlanningCycleId();

      const [costcenters, registerResult, workbenchResult] =
        await Promise.all([
          fetchCostCenters(pci, null),
          fetchActivityBudgetRegister(pci, null),
          fetchActivityBudgetWorkbench(pci),
        ]);

      const control = workbenchResult.control;

      this.setState({
        costcenters: costcenters || [],
        control,

        register: registerResult.rows || [],
        summary: registerResult.summary || {},

        header: workbenchResult.header || registerResult.header || {},

        actions: normalizeActions(workbenchResult.actions),

        selectedCostCenter: null,

        activitySearch: '',
        lineItemSearch: '',

        activities: [],
        lineitems: [],
        links: [],

        selectedActivities: emptySelection(),
        selectedLineItems: emptySelection(),
        selectedLinks: emptySelection(),
      });
    } catch (e: any) {
      this.setState({
        error: e?.message || String(e),
        actions: emptyActions(),
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  /* ============================================================
   * CLEAR ALLOCATOR
   * ============================================================ */

  private clearAllocator = () => {
    this.setState({
      activities: [],
      lineitems: [],
      links: [],

      selectedActivities: emptySelection(),
      selectedLineItems: emptySelection(),
      selectedLinks: emptySelection(),
    });
  };

  /* ============================================================
   * REFRESH WORKSPACE
   *
   * Always refresh both lifecycle and register authority.
   * ============================================================ */

  refreshWorkspace = async () => {
    const pci = this.getPlanningCycleId();

    const { selectedCostCenter, activitySearch, lineItemSearch } = this.state;

    this.setState({
      loading: true,
      error: '',
    });

    try {
      const [registerResult, workbenchResult] =
        await Promise.all([
          fetchActivityBudgetRegister(pci, selectedCostCenter),
          fetchActivityBudgetWorkbench(pci),
        ]);

      const control = workbenchResult.control;

      const commonState = {
        register: registerResult.rows || [],
        summary: registerResult.summary || {},

        header: workbenchResult.header || registerResult.header || {},

        actions: normalizeActions(workbenchResult.actions),

        control,

        selectedActivities: emptySelection(),
        selectedLineItems: emptySelection(),
        selectedLinks: emptySelection(),
      };

      if (!selectedCostCenter) {
        this.setState({
          ...commonState,

          activities: [],
          lineitems: [],
          links: [],
        });

        return;
      }

      const budgetplanid = this.budgetplanid;

      if (!budgetplanid) {
        this.setState({
          ...commonState,

          activities: [],
          lineitems: [],
          links: [],

          error:
            'The selected Cost Centre has no prepared Budget Plan. Run Activity Budget preparation first.',
        });

        return;
      }

      const [activities, lineitems, links] = await Promise.all([
        fetchActivities(pci, selectedCostCenter, activitySearch, budgetplanid),

        fetchLineItems(pci, selectedCostCenter, lineItemSearch, budgetplanid),

        fetchLinks(pci, selectedCostCenter, budgetplanid),
      ]);

      this.setState({
        ...commonState,

        activities,
        lineitems,
        links,
      });
    } catch (e: any) {
      this.setState({
        error: e?.message || String(e),
        actions: emptyActions(),
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  /* ============================================================
   * ALLOCATOR-ONLY REFRESH
   * ============================================================ */

  refreshAllocator = async () => {
    const pci = this.getPlanningCycleId();

    const { selectedCostCenter, activitySearch, lineItemSearch } = this.state;

    if (!selectedCostCenter) {
      this.clearAllocator();
      return;
    }

    const budgetplanid = this.budgetplanid;

    if (!budgetplanid) {
      this.clearAllocator();
      return;
    }

    this.setState({
      loading: true,
      error: '',
    });

    try {
      const [activities, lineitems, links] = await Promise.all([
        fetchActivities(pci, selectedCostCenter, activitySearch, budgetplanid),

        fetchLineItems(pci, selectedCostCenter, lineItemSearch, budgetplanid),

        fetchLinks(pci, selectedCostCenter, budgetplanid),
      ]);

      this.setState({
        activities,
        lineitems,
        links,

        selectedActivities: emptySelection(),
        selectedLineItems: emptySelection(),
        selectedLinks: emptySelection(),
      });
    } catch (e: any) {
      this.setState({
        error: e?.message || String(e),
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  /* ============================================================
   * FILTER HANDLERS
   * ============================================================ */

  onCostCenterChange = (selectedCostCenter: number | null) => {
    this.setState(
      {
        selectedCostCenter,

        activitySearch: '',
        lineItemSearch: '',

        selectedActivities: emptySelection(),
        selectedLineItems: emptySelection(),
        selectedLinks: emptySelection(),
      },
      this.refreshWorkspace
    );
  };

  onActivitySearchChange = (activitySearch: string) => {
    this.setState({ activitySearch }, this.refreshAllocator);
  };

  onLineItemSearchChange = (lineItemSearch: string) => {
    this.setState({ lineItemSearch }, this.refreshAllocator);
  };

  /* ============================================================
   * OPEN ALLOCATION
   * ============================================================ */

  openAllocate = () => {
    const s = this.state;

    if (!this.canMutate() || !s.actions.canAllocate) {
      this.setState({
        error: 'Activity Budget allocation is not currently permitted.',
      });
      return;
    }

    if (!s.selectedCostCenter) {
      this.setState({
        error: 'Select a Cost Centre before allocating.',
      });
      return;
    }

    if (!s.control.budgetcontrolid) {
      this.setState({
        error:
          'An active Activity Budget Control ceiling is required before allocation.',
      });
      return;
    }

    if (String(s.control.controlstatus).toUpperCase() === 'EXCEEDED') {
      this.setState({
        error:
          'The Activity Budget Control ceiling is already exceeded. Revise Budget Control before allocating.',
      });
      return;
    }

    if (
      selectionCount(s.selectedActivities) === 0 ||
      selectionCount(s.selectedLineItems) === 0
    ) {
      this.setState({
        error: 'Select at least one Activity Plan and one Budget Account.',
      });
      return;
    }

    this.setState({
      dialogOpen: true,
      error: '',
    });
  };

  /* ============================================================
   * PROCESS ALLOCATION
   * ============================================================ */

  submitAllocation = async (mode: AllocationMode, totalamount: number) => {
    if (!this.canMutate() || !this.state.actions.canAllocate) {
      this.setState({
        error: 'Activity Budget allocation is not currently permitted.',
      });
      return;
    }

    const activityplanids = selectionIds(this.state.selectedActivities);

    const lineitemids = selectionIds(this.state.selectedLineItems);

    const budgetplanid = this.budgetplanid;

    if (!budgetplanid) {
      this.setState({
        error: 'The selected Cost Centre has no prepared Budget Plan.',
      });
      return;
    }

    if (
      !this.state.selectedCostCenter ||
      !activityplanids.length ||
      !lineitemids.length
    ) {
      this.setState({
        error:
          'Select a Cost Centre, at least one Activity Plan and at least one account.',
      });
      return;
    }

    if (activityplanids.length > 1 && lineitemids.length > 1) {
      this.setState({
        error:
          'Many-to-many allocation requires the allocation matrix and is not available in the quick dialog.',
      });
      return;
    }

    this.setState({
      loading: true,
      error: '',
      success: '',
    });

    try {
      await processAllocation({
        planningcycleid: this.getPlanningCycleId(),
        costcenterid: this.state.selectedCostCenter,
        budgetplanid,
        activityplanids,
        lineitemids,
        mode,
        totalamount,
      });

      this.setState({
        success: `Allocation processed for ${activityplanids.length} Activity Plan(s) and ${lineitemids.length} account(s).`,
        dialogOpen: false,
      });

      await this.refreshWorkspace();
    } catch (e: any) {
      this.setState({
        error: e?.message || String(e),
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  /* ============================================================
   * UNMAP
   * ============================================================ */

  unmapSelected = async () => {
    if (!this.canMutate() || !this.state.actions.canUnmap) {
      this.setState({
        error: 'Activity Budget unmapping is not currently permitted.',
      });
      return;
    }

    const ids = selectionIds(this.state.selectedLinks);

    if (!ids.length) {
      this.setState({
        error: 'Select one or more allocation links to unmap.',
      });
      return;
    }

    this.setState({
      loading: true,
      error: '',
      success: '',
    });

    try {
      await unmapLinks(this.getPlanningCycleId(), ids);

      this.setState({
        success: `${ids.length} allocation link(s) unmapped.`,
      });

      await this.refreshWorkspace();
    } catch (e: any) {
      this.setState({
        error: e?.message || String(e),
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  /* ============================================================
   * SUBMIT ACTIVITY BUDGET
   * ============================================================ */

  submitActivityBudget = async () => {
    const { header, summary, actions } = this.state;

    const statecode = String(header.statecode || '').toUpperCase();

    if (statecode !== 'DRAFT') {
      this.setState({
        error: 'Only a Draft Activity Budget may be submitted.',
      });
      return;
    }

    if (!actions.canSubmit) {
      this.setState({
        error:
          'Activity Budget submission is not currently permitted. Complete the required correction and ensure the register is ready.',
      });
      return;
    }

    if (!summary.cansubmit) {
      this.setState({
        error:
          'Activity Budget is not yet ready for submission. Resolve pending financialisation items first.',
      });
      return;
    }

    if (!header.stamp) {
      this.setState({
        error:
          'Activity Budget concurrency stamp is missing. Refresh and try again.',
      });
      return;
    }

    if (
      !window.confirm(
        'Submit Activity Budget? Allocation and unmapping will become read-only until the budget is reopened.'
      )
    ) {
      return;
    }

    this.setState({
      loading: true,
      error: '',
      success: '',
    });

    try {
      await transitionActivityBudget({
        planningcycleid: this.getPlanningCycleId(),
        action: 'SUBMIT',
        reason: null,
        stamp: header.stamp,
      });

      this.setState({
        success: 'Activity Budget submitted successfully.',

        dialogOpen: false,

        selectedActivities: emptySelection(),
        selectedLineItems: emptySelection(),
        selectedLinks: emptySelection(),
      });

      await this.refreshWorkspace();
    } catch (e: any) {
      this.setState({
        error: e?.message || String(e),
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  /* ============================================================
   * REOPEN ACTIVITY BUDGET
   * ============================================================ */

  reopenActivityBudget = async () => {
    const { header, actions } = this.state;

    const statecode = String(header.statecode || '').toUpperCase();

    if (!['SUBMITTED', 'RETURNED'].includes(statecode)) {
      this.setState({
        error: 'Only a Submitted or Returned Activity Budget may be reopened.',
      });
      return;
    }

    if (!actions.canReopen) {
      this.setState({
        error: 'Reopening this Activity Budget is not currently permitted.',
      });
      return;
    }

    if (!header.stamp) {
      this.setState({
        error:
          'Activity Budget concurrency stamp is missing. Refresh and try again.',
      });
      return;
    }

    let reason: string | null = null;

    if (statecode === 'SUBMITTED') {
      reason = window.prompt('Reason for reopening Activity Budget:');

      if (reason === null) return;

      if (!reason.trim()) {
        this.setState({
          error: 'A reason is required to reopen a Submitted Activity Budget.',
        });
        return;
      }
    } else {
      const confirmed = window.confirm(
        'Reopen this Returned Activity Budget for correction? This will move it to Draft and enable allocation and unmapping.'
      );

      if (!confirmed) return;

      reason = 'Reopened returned Activity Budget for correction.';
    }

    this.setState({
      loading: true,
      error: '',
      success: '',
    });

    try {
      await transitionActivityBudget({
        planningcycleid: this.getPlanningCycleId(),
        action: 'REOPEN',
        reason: reason?.trim() || null,
        stamp: header.stamp,
      });

      this.setState({
        success: 'Activity Budget reopened for editing.',
      });

      await this.refreshWorkspace();
    } catch (e: any) {
      this.setState({
        error: e?.message || String(e),
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  /* ============================================================
   * RENDER
   * ============================================================ */

  render() {
    const s = this.state;

    const active = Boolean(s.selectedCostCenter);

    const lifecycleState = String(s.header.statecode || '').toUpperCase();

    const canMutate = lifecycleState === 'DRAFT';

    const canAllocate = canMutate && s.actions.canAllocate;

    const canUnmap = canMutate && s.actions.canUnmap;

    const canOpenAllocate =
      active &&
      canAllocate &&
      selectionCount(s.selectedActivities) > 0 &&
      selectionCount(s.selectedLineItems) > 0;

    const filters = (
      <BudgetAllocationFilterBar
        costcenters={s.costcenters}
        selectedCostCenter={s.selectedCostCenter}
        activitySearch={s.activitySearch}
        lineItemSearch={s.lineItemSearch}
        loading={s.loading}
        onCostCenterChange={this.onCostCenterChange}
        onActivitySearchChange={this.onActivitySearchChange}
        onLineItemSearchChange={this.onLineItemSearchChange}
        onRefresh={this.refreshWorkspace}
      />
    );

    return (
      <PlanningWorkbenchShell
        {...this.props}
        onBack={this.props.onBack}
        headerExtension={
          <FPlanningJourney
            phases={s.phases}
            currentPhaseCode={'BUDGET ALLOCATION'}
          />
        }
        title={this.props.title || 'Activity Budget'}
        subtitle="Financialise eligible Activity Plans against Budget Accounts within the active Activity Budget Control ceiling."
        icon={<AccountBalanceOutlinedIcon />}
        loading={s.loading}
        error={s.error}
        onRefresh={this.refreshWorkspace}
        filters={filters}
      >
        <Box sx={{ p: 3 }}>
          {/* ==================================================
           * HEADER
           * ================================================== */}

          <Stack sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Activity Budget Workbench
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Review the cycle-wide Activity Budget register, then select a Cost
              Centre to allocate eligible Activity Plans to six-digit Budget
              Plan accounts.
            </Typography>
          </Stack>

          {/* ==================================================
           * MESSAGES
           * ================================================== */}

          {s.error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {s.error}
            </Alert>
          )}

          {s.success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {s.success}
            </Alert>
          )}

          {s.warning && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {s.warning}
            </Alert>
          )}

          <Stack spacing={3}>
            {/* ================================================
             * SUMMARY + GOVERNED LIFECYCLE ACTIONS
             * ================================================ */}

            <BudgetAllocationSummaryPanel
              summary={s.summary}
              control={s.control}
              header={s.header}
              loading={s.loading}
              canSubmit={s.actions.canSubmit}
              canReopen={s.actions.canReopen}
              onSubmit={this.submitActivityBudget}
              onReopen={this.reopenActivityBudget}
            />

            {/* ================================================
             * CYCLE-WIDE REGISTER
             * ================================================ */}

            <ActivityBudgetRegisterGrid rows={s.register} loading={s.loading} />

            {/* ================================================
             * ALLOCATION WORKSPACE
             * ================================================ */}

            {!active ? (
              <Alert severity="info">
                The Activity Budget register above is cycle-wide. Select a Cost
                Centre to open its allocation workspace.
              </Alert>
            ) : (
              <Stack spacing={3}>
                {!canMutate && (
                  <Alert severity="info">
                    {lifecycleState === 'RETURNED'
                      ? 'Activity Budget has been Returned for correction and is read-only. Reopen it to Draft before changing allocations or mappings.'
                      : 'Activity Budget is Submitted and read-only. Reopen it before changing allocations or mappings.'}
                  </Alert>
                )}

                <Paper elevation={1} sx={{ p: 2 }}>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Allocation Workspace
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      Select Activity Plans on the left and Budget Accounts on
                      the right. Allocate acts on those selections; Unmap acts
                      only on existing mappings below.
                    </Typography>
                  </Stack>
                </Paper>

                <Grid container spacing={2} alignItems="stretch">
                  {/* ACTIVITY PLANS */}

                  <Grid
                    size={{
                      xs: 12,
                      lg: 5,
                    }}
                  >
                    <BudgetAllocationActivityGrid
                      rows={s.activities}
                      selection={s.selectedActivities}
                      loading={s.loading}
                      onSelectionChange={(selectedActivities) =>
                        this.setState({
                          selectedActivities,
                        })
                      }
                    />
                  </Grid>

                  {/* ALLOCATE ACTION */}

                  <Grid
                    size={{
                      xs: 12,
                      lg: 2,
                    }}
                  >
                    <Stack
                      justifyContent="center"
                      alignItems="center"
                      sx={{
                        height: '100%',
                        minHeight: {
                          lg: 520,
                        },
                      }}
                    >
                      <Button
                        variant="contained"
                        startIcon={<LinkIcon />}
                        disabled={s.loading || !canOpenAllocate}
                        onClick={this.openAllocate}
                        sx={{
                          minWidth: 140,
                        }}
                      >
                        Allocate
                      </Button>
                    </Stack>
                  </Grid>

                  {/* BUDGET ACCOUNTS */}

                  <Grid
                    size={{
                      xs: 12,
                      lg: 5,
                    }}
                  >
                    <BudgetAllocationLineItemGrid
                      rows={s.lineitems}
                      selection={s.selectedLineItems}
                      loading={s.loading}
                      onSelectionChange={(selectedLineItems) =>
                        this.setState({
                          selectedLineItems,
                        })
                      }
                    />
                  </Grid>
                </Grid>

                {/* EXISTING MAPPINGS */}

                <BudgetAllocationLinksGrid
                  rows={s.links}
                  selection={s.selectedLinks}
                  loading={s.loading}
                  mutationDisabled={!canUnmap}
                  onSelectionChange={(selectedLinks) =>
                    this.setState({
                      selectedLinks,
                    })
                  }
                  onUnmapSelected={this.unmapSelected}
                />
              </Stack>
            )}
          </Stack>

          {/* ==================================================
           * ALLOCATION DIALOG
           * ================================================== */}

          <BudgetAllocationBulkDialog
            open={s.dialogOpen}
            activityCount={selectionCount(s.selectedActivities)}
            lineItemCount={selectionCount(s.selectedLineItems)}
            controlAvailable={s.control.availableamount}
            onClose={() =>
              this.setState({
                dialogOpen: false,
              })
            }
            onSubmit={this.submitAllocation}
          />
        </Box>
      </PlanningWorkbenchShell>
    );
  }
}

export default BudgetAllocationWorkbench;
